/*
 * Duck Huntz — retro shooting game, ported to a Shopify Web Component.
 * Source: DUCK-HUNTZ-ADVANCED/duckhuntz-advanced.html (game by Alan, FINAL_v2).
 *
 * The game loop / physics / draw routines are a faithful port of the original.
 * What changed for the theme:
 *   - Wrapped in a <duck-huntz-custom> custom element (one self-contained
 *     instance per section, no global ids).
 *   - Config (coupons, round data, asset CDN urls, screen text) is read from
 *     a JSON island referenced by the element's `data-config` attribute.
 *   - connectedCallback boots the game; disconnectedCallback cancels the rAF
 *     loop and removes every listener so the theme editor can re-render the
 *     section without leaking loops/listeners.
 *
 * v2 gameplay (Aug 2026):
 *   - Ducks rise out of the foreground grass instead of popping in.
 *   - A miss spooks every duck on screen — they bolt upward (escape sprite).
 *   - Round 3 launches two ducks with different movement personalities; the
 *     double-bull sprite celebrates a clean pair.
 *   - Beating round 3 awards the bonus coupon. Missing out in round 3 awards
 *     the base coupon plus a one-time "Try for Bonus" replay of round 3.
 */
"use strict";
(function () {
  if (window.customElements.get("duck-huntz-custom")) return;

  function loadImg(src) {
    return new Promise(function (res, rej) {
      var i = new Image();
      i.onload = function () { res(i); };
      i.onerror = function () { rej(new Error("Failed: " + src)); };
      i.src = src;
    });
  }
  function loadImgOptional(src) {
    if (!src) return Promise.resolve(null);
    return loadImg(src).catch(function () { return null; });
  }

  class DuckHuntzCustom extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var host = this;

      /* ---- CONFIG ---- */
      var cfg = {};
      try {
        var cfgEl = document.getElementById(host.getAttribute("data-config"));
        cfg = JSON.parse(cfgEl.textContent);
      } catch (e) { cfg = {}; }

      var ROUNDS = cfg.rounds || [];
      var ASSETS_SRC = cfg.images || {};
      var SFX_SRC = cfg.sounds || {};
      var COUPON_WIN = cfg.couponWin || "GEARUPBONUS";
      var COUPON_FAIL = cfg.couponComplete || "GEARUP15";
      var ACTIVE_COUPON = COUPON_WIN;
      var TXT = {
        startHint: cfg.startHint || "3 ROUNDS - SHOOT THE DUCKS - WIN A COUPON!",
        winTitle: cfg.winTitle || "NICE SHOOTING, YOU WON!",
        winHint: cfg.winHint || "YOU UNLOCKED THE GEAR UP BONUS - 20% OFF!",
        completeTitle: cfg.completeTitle || "NICE SHOOTING, HUNT COMPLETE!",
        completeHint: cfg.completeHint || "YOUR GEAR UP REWARD: 15% OFF",
        bonusButton: cfg.bonusButton || "Try for Bonus",
        playAgain: "Play again",
        bonusSplashLabel: cfg.bonusSplashLabel || "SO CLOSE!",
        bonusSplashSub: cfg.bonusSplashSub || "ONE MORE SHOT AT THE BONUS REWARD."
      };

      /* Game-internal render resolution (4:3, matches Alan's v2 build; all
         flight-band constants below are tuned to it). */
      var GW = 683, GH = 512;
      var BULL_MS = 950, BULL_RISE = 30, BULL_W = 213, BULL_X = 0.5;
      var FLUTTER_AMP = 10, FLUTTER_HZ = 1.2;
      var ESCAPE_SPD = 880;
      var YMIN = 90, YMAX = 360, YBIAS = -120;
      var TURN_MS = 220;
      var TOUCH_HIT_BONUS = 22; // extra forgiving hitbox for touch input only
      var RISE_START_PAD = 52, RISE_MIN_Y = 318, RISE_MAX_Y = 350;
      var RISE_JUKE_MIN_MS = 90, RISE_JUKE_MAX_MS = 175, RISE_WOBBLE_AMP = 95, RISE_WOBBLE_HZ = 3.4;
      var HIT_MS = 200, MISS_MS = 340, SHAKE_MS = 260;
      var SPLASH_MS = 2000, CD_STEP = 850;
      var GRACE_MS = 750; // window after the first hit to also land the second before it flees
      var LAST_ROUND = ROUNDS.length - 1;

      /* ---- ELEMENT REFS (scoped to this instance) ---- */
      var canvas = host.querySelector('[data-dh="game"]');
      var ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;

      var modal = host.querySelector('[data-dh="modal"]');
      var card = host.querySelector('[data-dh="card"]');
      var scorePill = host.querySelector('[data-dh="scorePill"]');
      var startRow = host.querySelector('[data-dh="startRow"]');
      var codeRow = host.querySelector('[data-dh="codeRow"]');
      var codeText = host.querySelector('[data-dh="codeText"]');
      var copyBtn = host.querySelector('[data-dh="copyBtn"]');
      var playAgainBtn = host.querySelector('[data-dh="playAgainBtn"]');
      var startBtn = host.querySelector('[data-dh="startBtn"]');
      var startHint = host.querySelector('[data-dh="startHint"]');
      var couponHint = host.querySelector('[data-dh="couponHint"]');

      var dead = false;
      var rafId = 0;
      var timers = [];
      function later(fn, ms) {
        var id = setTimeout(function () {
          if (dead) return;
          fn();
        }, ms);
        timers.push(id);
        return id;
      }

      function rand(a, b) { return Math.random() * (b - a) + a; }
      function now() { return performance.now(); }

      function resizeCv() {
        var dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
        canvas.width = GW * dpr; canvas.height = GH * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.imageSmoothingEnabled = false;
      }

      function ptGame(e) {
        var r = canvas.getBoundingClientRect();
        var cx = e.touches ? e.touches[0].clientX : e.clientX;
        var cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: (cx - r.left) * (GW / r.width), y: (cy - r.top) * (GH / r.height) };
      }

      function drawCover(img) {
        var sc = Math.max(GW / img.width, GH / img.height);
        ctx.drawImage(img, (GW - img.width * sc) / 2, (GH - img.height * sc) / 2, img.width * sc, img.height * sc);
      }
      function drawGrass(img) {
        var sc = GW / img.width;
        ctx.drawImage(img, 0, GH - img.height * sc, GW, img.height * sc);
      }

      /* ---- AUDIO ---- */
      var SFX = {}, audioOk = false, audioCtx = null;
      // Keep countdown visuals on mobile, but silence countdown beeps there.
      var IS_MOBILE = (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
        ((navigator.maxTouchPoints || 0) > 0 && Math.min(window.screen.width || 9999, window.screen.height || 9999) <= 1024));
      function armAudio() {
        if (audioOk) return; audioOk = true;
        for (var k in SFX_SRC) {
          var a = new Audio(); a.src = SFX_SRC[k]; a.preload = "auto"; a.volume = 0.9; SFX[k] = a;
        }
        try { audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) {}
      }
      function beep(freq, dur, vol, type) {
        if (!audioCtx) return;
        try {
          var o = audioCtx.createOscillator();
          var g = audioCtx.createGain();
          o.connect(g); g.connect(audioCtx.destination);
          o.type = type || "square";
          o.frequency.setValueAtTime(freq, audioCtx.currentTime);
          g.gain.setValueAtTime(vol || 0.18, audioCtx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + dur);
          o.start(audioCtx.currentTime);
          o.stop(audioCtx.currentTime + dur);
        } catch (e) {}
      }
      function sfx(k) {
        var a = SFX[k]; if (!a) return;
        try { a.currentTime = 0; a.play().catch(function () {}); } catch (e) {}
      }

      /* ---- STATE ---- */
      var ri = 0;
      function R() { return ROUNDS[ri]; }

      var S = {
        mode: "menu",
        hits: 0, miss: 0,
        d1: null, d2: null,
        pairSpawn: 0,
        h1: false, hs1: 0, sx1: 0, sy1: 0, sf1: -1,
        h2: false, hs2: 0, sx2: 0, sy2: 0, sf2: -1,
        pairN: 0, pairDeadline: 0, resolveAt: 0,
        bullOn: false, bullT: 0, bullCount: 0,
        escs: [],
        hfAt: 0, hfX: 0, hfY: 0,
        mfAt: 0, shAt: 0,
        spAt: 0, spOn: false, spLbl: "", spSub: "", spCb: null,
        cdAt: 0, cdOn: false, cdCb: null, cdLastBeep: -1,
        flts: [], conf: [], confOn: false,
        failPending: false, failResetAt: 0, failAction: "",
        lastRoundRetries: 0
      };

      function setHUD() {
        var r = R();
        scorePill.textContent = "ROUND " + (ri + 1) + "  |  Hits: " + S.hits + "/" + r.hitsToWin + "  |  Misses: " + S.miss + "/" + r.missLimit;
      }

      /* ---- MENU SCREENS ---- */
      function showMenu() {
        S.mode = "menu";
        modal.style.display = "flex";
        startRow.style.display = "flex";
        startHint.style.display = "block";
        startHint.textContent = TXT.startHint;
        playAgainBtn.textContent = TXT.playAgain;
        codeRow.style.display = "none";
        couponHint.style.display = "none";
        setHUD();
      }

      function showWin() {
        S.mode = "end";
        modal.style.display = "flex";
        startRow.style.display = "none";
        ACTIVE_COUPON = COUPON_WIN;
        startHint.textContent = TXT.winTitle;
        startHint.style.display = "block";
        codeText.textContent = ACTIVE_COUPON;
        couponHint.textContent = TXT.winHint;
        couponHint.style.display = "block";
        playAgainBtn.textContent = TXT.playAgain;
        codeRow.style.display = "flex";
        sfx("win");
        spawnConf();
      }

      function showHuntComplete() {
        // Completing the hunt earns the base Gear Up offer. After the first
        // final-round miss-out, the player may voluntarily retry that round
        // once for the bonus. If that also fails, the next replay starts over.
        S.mode = "end";
        modal.style.display = "flex";
        startRow.style.display = "none";
        ACTIVE_COUPON = COUPON_FAIL;
        startHint.textContent = TXT.completeTitle;
        startHint.style.display = "block";
        codeText.textContent = ACTIVE_COUPON;
        couponHint.textContent = TXT.completeHint;
        couponHint.style.display = "block";
        playAgainBtn.textContent = canRetryBonus() ? TXT.bonusButton : TXT.playAgain;
        codeRow.style.display = "flex";
        sfx("win");
        spawnConf();
      }
      function canRetryBonus() {
        return ri === LAST_ROUND && S.lastRoundRetries < 1;
      }

      /* ---- CONFETTI ---- */
      var CCOLS = ["#ff6a13", "#ffe600", "#00e5ff", "#ff3d6e", "#69ff47", "#ffffff"];
      function spawnConf() {
        S.conf = []; S.confOn = true;
        for (var i = 0; i < 120; i++) {
          S.conf.push({
            x: rand(GW * 0.15, GW * 0.85), y: rand(-80, -5),
            vx: rand(-130, 130), vy: rand(160, 420),
            rot: rand(0, Math.PI * 2), rv: rand(-5, 5),
            w: rand(7, 16), h: rand(4, 9),
            col: CCOLS[Math.floor(rand(0, CCOLS.length))], life: 1
          });
        }
      }
      function upConf(dt) {
        if (!S.confOn) return;
        var alive = false;
        for (var i = 0; i < S.conf.length; i++) {
          var c = S.conf[i];
          c.x += c.vx * dt; c.y += c.vy * dt; c.rot += c.rv * dt; c.life -= dt * 0.36;
          if (c.life > 0) alive = true;
        }
        if (!alive) S.confOn = false;
      }
      function drConf() {
        for (var i = 0; i < S.conf.length; i++) {
          var c = S.conf[i]; if (c.life <= 0) continue;
          ctx.save(); ctx.globalAlpha = Math.max(0, Math.min(1, c.life));
          ctx.translate(c.x, c.y); ctx.rotate(c.rot);
          ctx.fillStyle = c.col; ctx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
          ctx.restore();
        }
      }

      /* ---- FLOATERS ---- */
      function addFlt(x, y, txt, col) {
        S.flts.push({ x: x, y: y, vy: -85, life: 1, txt: txt || "+1", col: col || "#ffe600" });
      }
      function upFlts(dt) {
        var n = [];
        for (var i = 0; i < S.flts.length; i++) {
          var f = S.flts[i]; f.y += f.vy * dt; f.life -= dt * 1.5;
          if (f.life > 0) n.push(f);
        }
        S.flts = n;
      }
      function drFlts() {
        for (var i = 0; i < S.flts.length; i++) {
          var f = S.flts[i];
          ctx.save(); ctx.globalAlpha = Math.max(0, Math.min(1, f.life * 1.3));
          ctx.font = "19px 'DuckHuntz Press Start 2P'"; ctx.textAlign = "center";
          ctx.fillStyle = "#000"; ctx.fillText(f.txt, f.x + 2, f.y + 2);
          ctx.fillStyle = f.col; ctx.fillText(f.txt, f.x, f.y);
          ctx.restore();
        }
      }

      /* ---- SPLASH ---- */
      function showSplash(lbl, sub, cb) {
        S.spOn = true; S.spLbl = lbl; S.spSub = sub; S.spAt = now(); S.spCb = cb || null;
      }
      function drSplash(t) {
        if (!S.spOn) return;
        var el = t - S.spAt;
        if (el >= SPLASH_MS) {
          S.spOn = false;
          if (S.spCb) { var cb = S.spCb; S.spCb = null; cb(); }
          return;
        }
        var p = el / SPLASH_MS;
        var al = p < 0.15 ? p / 0.15 : p > 0.80 ? (1 - p) / 0.20 : 1;
        al = Math.max(0, Math.min(1, al));
        var sx = p < 0.12 ? (1 - p / 0.12) * (-GW * 0.3) : 0;
        ctx.save();
        ctx.globalAlpha = al * 0.75; ctx.fillStyle = "#000";
        ctx.fillRect(0, GH / 2 - 70, GW, 140);
        ctx.globalAlpha = al; ctx.translate(sx, 0);
        ctx.fillStyle = "#ff6a13"; ctx.fillRect(0, GH / 2 - 70, 8, 140);
        ctx.font = "43px 'DuckHuntz Press Start 2P'"; ctx.textAlign = "center";
        ctx.shadowColor = "#ff6a13"; ctx.shadowBlur = 20;
        ctx.fillStyle = "#fff"; ctx.fillText(S.spLbl, GW / 2, GH / 2 - 4);
        ctx.shadowBlur = 0;
        ctx.font = "18px 'DuckHuntz Press Start 2P'"; ctx.fillStyle = "#ff6a13";
        ctx.fillText(S.spSub, GW / 2, GH / 2 + 34);
        ctx.restore();
      }

      /* ---- COUNTDOWN ---- */
      function startCD(cb) {
        S.cdOn = true; S.cdAt = now(); S.cdCb = cb || null;
        S.cdLastBeep = -1;
      }
      function upCD(t) {
        if (!S.cdOn) return;
        var el = t - S.cdAt;
        var step = Math.floor(el / CD_STEP);
        if (step !== S.cdLastBeep && step <= 3) {
          S.cdLastBeep = step;
          if (!IS_MOBILE) {
            if (step < 3) {
              beep(220 + (step * 55), 0.12, 0.22, "square");
            } else {
              beep(660, 0.08, 0.28, "square");
              later(function () { beep(880, 0.15, 0.22, "square"); }, 80);
            }
          }
        }
        if (el >= CD_STEP * 4) {
          S.cdOn = false;
          if (S.cdCb) { var cb = S.cdCb; S.cdCb = null; cb(); }
        }
      }
      function drCD(t) {
        if (!S.cdOn) return;
        var el = t - S.cdAt;
        var step = Math.floor(el / CD_STEP);
        var frac = (el % CD_STEP) / CD_STEP;
        var lbl = step < 3 ? String(3 - step) : "GO!";
        var col = step < 3 ? "#ffe600" : "#ff6a13";
        var sc = 1 + (1 - frac) * 0.55;
        var al = frac > 0.7 ? (1 - frac) / 0.3 : 1;
        ctx.save(); ctx.globalAlpha = Math.max(0, al);
        ctx.translate(GW / 2, GH / 2 - 20); ctx.scale(sc, sc);
        ctx.font = "77px 'DuckHuntz Press Start 2P'"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillStyle = "#000"; ctx.fillText(lbl, 3, 3);
        ctx.fillStyle = col; ctx.fillText(lbl, 0, 0);
        ctx.restore();
      }

      /* ---- SHAKE ---- */
      function doShake() { S.shAt = now(); }
      function getShake(t) {
        if (!S.shAt) return { x: 0, y: 0 };
        var e = t - S.shAt; if (e > SHAKE_MS) { S.shAt = 0; return { x: 0, y: 0 }; }
        var p = 1 - e / SHAKE_MS;
        return { x: (Math.random() - 0.5) * 14 * p, y: (Math.random() - 0.5) * 8 * p };
      }

      /* ---- MISS FLASH ---- */
      function drMissFlash(t) {
        if (!S.mfAt) return;
        var e = t - S.mfAt; if (e > MISS_MS) { S.mfAt = 0; return; }
        var p = 1 - e / MISS_MS;
        ctx.save(); ctx.globalAlpha = 0.40 * p;
        var g = ctx.createRadialGradient(GW / 2, GH / 2, GH * 0.3, GW / 2, GH / 2, GH * 0.9);
        g.addColorStop(0, "transparent"); g.addColorStop(1, "#ff1a1a");
        ctx.fillStyle = g; ctx.fillRect(0, 0, GW, GH);
        ctx.restore();
      }

      /* ---- HIT FLASH ---- */
      function drHitFlash(t) {
        if (!S.hfAt) return;
        var e = t - S.hfAt; if (e > HIT_MS) { S.hfAt = 0; return; }
        var p = 1 - e / HIT_MS;
        ctx.save(); ctx.globalAlpha = 0.5 * p;
        ctx.beginPath(); ctx.arc(S.hfX, S.hfY, 20 + (1 - p) * 18, 0, Math.PI * 2);
        ctx.fillStyle = "#fff"; ctx.fill();
        ctx.restore();
      }

      /* ---- ROUND FLOW ---- */
      function resetGame() {
        ri = 0;
        S.lastRoundRetries = 0;
        startRound();
      }

      function startRound(overrideLbl, overrideSub) {
        S.mode = "splash";
        S.hits = 0; S.miss = 0;
        S.d1 = null; S.d2 = null;
        S.h1 = false; S.h2 = false;
        S.escs = [];
        S.pairN = 0; S.pairDeadline = 0; S.resolveAt = 0;
        S.bullOn = false; S.bullCount = 0;
        S.hfAt = 0; S.mfAt = 0; S.shAt = 0;
        S.flts = []; S.conf = []; S.confOn = false;
        S.failPending = false; S.failResetAt = 0; S.failAction = "";
        modal.style.display = "none";
        setHUD();
        var r = R();
        showSplash(overrideLbl || r.lbl, overrideSub || r.sub, function () {
          startCD(function () { beginPlay(); });
        });
      }

      function beginPlay() {
        S.mode = "playing";
        S.pairSpawn = now() + 400;
        setHUD();
      }

      function nextRound() {
        if (ri >= LAST_ROUND) showWin();
        else { ri++; startRound(); }
      }

      /* ---- DUCK ---- */
      function newBurst(d, t) {
        var r = R();
        var speedScale = d.speedScale || 1;
        var spd = rand(r.spdMin, r.spdMax) * speedScale;
        var keepDirChance = typeof d.keepDirChance === "number" ? d.keepDirChance : 0.7;
        var pref = Math.random() < keepDirChance ? (d.vx >= 0 ? 1 : -1) : (Math.random() < 0.5 ? -1 : 1);
        d.tVx = pref * spd * rand(0.62, 1.18);
        d.tVy = YBIAS - spd * rand(0.35, 0.95) * 0.35 + rand(-90, 120) + (d.vertBias || 0);
        d.bEnd = t + rand(r.bMin, r.bMax) * (d.burstScale || 1);
      }
      function spawnDk(slot, xHint, profile) {
        var r = R();
        var t = now();
        profile = profile || {};
        var speedScale = profile.speedScale || 1;
        var spd = rand(r.spdMin, r.spdMax) * speedScale;
        var dir = typeof profile.dir === "number" ? profile.dir : (Math.random() < 0.5 ? -1 : 1);
        var jMin = profile.jukeMin || RISE_JUKE_MIN_MS;
        var jMax = profile.jukeMax || RISE_JUKE_MAX_MS;
        var d = {
          // Start below the visible playfield so the foreground grass masks the entrance.
          x: typeof xHint === "number" ? xHint : rand(120, GW - 120),
          y: GH + (r.dkSz / 2) + RISE_START_PAD,
          vx: 0, vy: 0,
          tVx: dir * spd * 0.9, tVy: YBIAS - spd * 0.25,
          bEnd: 0, facing: dir > 0 ? 1 : -1,
          w: r.dkSz, h: r.dkSz,
          fp: rand(0, Math.PI * 2), slot: slot,
          rising: true,
          riseStartAt: t + (profile.delay || 0),
          riseTo: rand(RISE_MIN_Y, RISE_MAX_Y) + (profile.riseToBias || 0),
          riseSpd: rand(425, 565) * (profile.riseSpeedScale || 1),
          riseVx: dir * rand(55, 115) * (profile.riseVxScale || 1),
          riseJukeAt: t + (profile.delay || 0) + rand(jMin, jMax),
          riseJukeDir: dir,
          risePhase: typeof profile.phase === "number" ? profile.phase : rand(0, Math.PI * 2),
          riseJukeMin: jMin, riseJukeMax: jMax,
          riseWobbleAmp: profile.wobbleAmp || RISE_WOBBLE_AMP,
          riseWobbleHz: profile.wobbleHz || RISE_WOBBLE_HZ,
          speedScale: speedScale,
          vertBias: profile.vertBias || 0,
          burstScale: profile.burstScale || 1,
          keepDirChance: typeof profile.keepDirChance === "number" ? profile.keepDirChance : 0.7
        };
        if (slot === 1) S.d1 = d; else S.d2 = d;
      }
      function spawnRoundDucks() {
        var r = R();
        if (!r.dual) {
          spawnDk(1);
          return;
        }

        // Dual round: give each duck a different movement personality so
        // they do not launch, juke, turn, or climb in lockstep.
        var fastFirst = Math.random() < 0.5;
        var p1 = {
          delay: rand(0, 35),
          speedScale: fastFirst ? rand(1.04, 1.14) : rand(0.90, 1.00),
          riseSpeedScale: fastFirst ? rand(1.03, 1.12) : rand(0.90, 1.00),
          riseVxScale: rand(0.85, 1.25),
          jukeMin: rand(65, 90), jukeMax: rand(120, 155),
          wobbleAmp: rand(70, 105), wobbleHz: rand(2.7, 3.5),
          vertBias: rand(-70, -15), burstScale: rand(0.72, 0.92),
          keepDirChance: rand(0.52, 0.66), phase: rand(0, Math.PI * 2),
          riseToBias: rand(-12, 4)
        };
        var p2 = {
          delay: rand(55, 110),
          speedScale: fastFirst ? rand(0.90, 1.00) : rand(1.04, 1.14),
          riseSpeedScale: fastFirst ? rand(0.90, 1.00) : rand(1.03, 1.12),
          riseVxScale: rand(0.85, 1.25),
          jukeMin: rand(105, 140), jukeMax: rand(175, 225),
          wobbleAmp: rand(105, 145), wobbleHz: rand(3.8, 4.8),
          vertBias: rand(15, 80), burstScale: rand(1.02, 1.28),
          keepDirChance: rand(0.72, 0.86), phase: p1.phase + rand(1.2, 2.6),
          riseToBias: rand(4, 18)
        };

        if (Math.random() < 0.5) {
          p1.dir = 1; p2.dir = -1;
          spawnDk(1, rand(125, GW * 0.42), p1);
          spawnDk(2, rand(GW * 0.58, GW - 125), p2);
        } else {
          p1.dir = -1; p2.dir = 1;
          spawnDk(1, rand(GW * 0.58, GW - 125), p1);
          spawnDk(2, rand(125, GW * 0.42), p2);
        }
      }
      function hitTest(mx, my, d, isTouch) {
        var pad = R().pad + (isTouch ? TOUCH_HIT_BONUS : 0);
        return mx >= d.x - (d.w / 2 + pad) && mx <= d.x + (d.w / 2 + pad) &&
          my >= d.y - (d.h / 2 + pad) && my <= d.y + (d.h / 2 + pad);
      }
      function drDuck(img, x, y, w, h, facing) {
        ctx.save(); ctx.translate(x, y);
        if (facing > 0) ctx.scale(-1, 1);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        ctx.restore();
      }

      function onHit(slot) {
        sfx("shot"); sfx("hit");
        var r = R();
        var d = slot === 1 ? S.d1 : S.d2;
        S.hfAt = now(); S.hfX = d.x; S.hfY = d.y;
        addFlt(d.x, d.y - d.h / 2, "+1", "#ffe600");
        if (slot === 1) { S.h1 = true; S.hs1 = now(); S.sx1 = d.x; S.sy1 = d.y; S.sf1 = d.facing; S.d1 = null; }
        else { S.h2 = true; S.hs2 = now(); S.sx2 = d.x; S.sy2 = d.y; S.sf2 = d.facing; S.d2 = null; }
        S.hits++; setHUD();

        if (S.pairN === 0) {
          S.pairN = 1;
          if (r.dual && (S.d1 || S.d2)) {
            // Partner still up - give the player a short window to also land it
            S.pairDeadline = now() + GRACE_MS;
          } else {
            // No partner (solo round, or it already left) - resolve after the normal pose beat
            S.resolveAt = now() + r.shotMs;
          }
        } else {
          // Second duck landed within the grace window - clean double
          S.pairN = 2;
          S.pairDeadline = 0;
          S.resolveAt = now() + r.shotMs;
        }
      }
      function escapeDuck(d) {
        if (!d) return;
        S.escs.push({ x: d.x, y: d.y, w: d.w, h: d.h, facing: d.facing });
      }
      function onMiss() {
        sfx("miss"); S.miss++; setHUD();
        S.mfAt = now(); doShake();
        addFlt(rand(GW * 0.3, GW * 0.7), GH * 0.42, "MISS!", "#ff4444");
        // A missed shot spooks whatever ducks are currently up - they bolt off screen.
        // Keep the game in PLAYING mode so the escape animation can finish updating.
        escapeDuck(S.d1); escapeDuck(S.d2);
        S.d1 = null; S.d2 = null;
        var r = R();

        if (S.miss >= r.missLimit) {
          // Final miss: stop all pair/round resolution and wait for every escape
          // sprite to clear the top of the screen before resolving the round.
          S.failPending = true;
          S.failResetAt = 0;
          S.pairDeadline = 0;
          S.resolveAt = 0;
          S.pairN = 0;
          S.h1 = false; S.h2 = false;
          return;
        }

        if (S.pairN === 1) {
          // Already had one kill this pair - the miss just cost you the double, resolve now.
          S.pairDeadline = 0;
          S.resolveAt = now();
        } else if (S.pairN === 0) {
          // Give the early rounds a clearer reset beat after a miss.
          S.pairSpawn = now() + rand(r.missSpMin || r.spMin, r.missSpMax || r.spMax);
        }
      }

      /* ---- BULL ---- */
      function trigBull() {
        S.h1 = false; S.h2 = false;
        S.bullOn = true; S.bullT = now(); S.bullCount = S.pairN;
        sfx("pop");
      }
      function checkRoundEnd(t) {
        var r = R();
        S.pairN = 0;
        if (S.hits >= r.hitsToWin) nextRound();
        else S.pairSpawn = t + rand(r.spMin, r.spMax);
      }
      function drBull(t) {
        if (!S.bullOn) return;
        var img = S.bullCount >= 2 ? A.bull2 : A.bull1;
        var el = t - S.bullT, p = Math.min(1, el / BULL_MS);
        var ud = p < 0.55 ? p / 0.55 : (1 - (p - 0.55) / 0.45);
        var ease = ud * ud * (3 - 2 * ud);
        var bW = BULL_W, bH = img.height * (bW / img.width);
        ctx.drawImage(img, (GW * BULL_X) - bW / 2, GH - 6 - (BULL_RISE * ease) - bH, bW, bH);
        if (p >= 1) {
          S.bullOn = false;
          checkRoundEnd(t);
        }
      }

      /* ---- INPUT ---- */
      function onPtr(e) {
        if (e.cancelable) e.preventDefault();
        armAudio();
        if (S.mode !== "playing") return;
        var p = ptGame(e);
        var isTouch = !!(e.touches && e.touches.length);
        var d1Active = S.d1 && !S.d1.rising;
        var d2Active = S.d2 && !S.d2.rising;
        if (d1Active && !S.h1 && hitTest(p.x, p.y, S.d1, isTouch)) { onHit(1); return; }
        if (d2Active && !S.h2 && hitTest(p.x, p.y, S.d2, isTouch)) { onHit(2); return; }
        // Don't charge a miss while the ducks are still hidden/rising behind the grass.
        if (d1Active || d2Active) onMiss();
      }
      function preventDefault(e) { e.preventDefault(); }
      function onStart() { armAudio(); resetGame(); }
      function onPlayAgain() {
        armAudio();
        // From the Hunt Complete screen, TRY FOR BONUS goes straight back to
        // the final round for one voluntary bonus attempt.
        if (S.mode === "end" && ACTIVE_COUPON === COUPON_FAIL && canRetryBonus()) {
          S.lastRoundRetries = 1;
          startRound(TXT.bonusSplashLabel, TXT.bonusSplashSub);
          return;
        }
        // Any other replay starts a fresh hunt from Round 1.
        resetGame();
      }
      function onCopy() {
        navigator.clipboard.writeText(ACTIVE_COUPON).then(function () {
          copyBtn.textContent = "Copied!";
          later(function () { copyBtn.textContent = "Copy"; }, 900);
        }).catch(function () { alert("Code: " + ACTIVE_COUPON); });
      }

      canvas.addEventListener("mousedown", onPtr, { passive: false });
      canvas.addEventListener("touchstart", onPtr, { passive: false });
      canvas.addEventListener("touchmove", preventDefault, { passive: false });
      canvas.addEventListener("touchend", preventDefault, { passive: false });
      canvas.addEventListener("contextmenu", preventDefault);
      startBtn.addEventListener("click", onStart);
      playAgainBtn.addEventListener("click", onPlayAgain);
      copyBtn.addEventListener("click", onCopy);
      window.addEventListener("resize", resizeCv);

      /* ---- LOOP ---- */
      var A = null, lastT = now();
      function tick() {
        if (dead) return;
        var t = now(), dt = Math.min(0.033, (t - lastT) / 1000); lastT = t;
        var r = R();

        if (S.mode === "playing") {
          if (!S.failPending && S.pairN === 1 && S.pairDeadline > 0 && t >= S.pairDeadline) {
            // Grace window expired - the partner didn't get hit in time, it bolts
            escapeDuck(S.d1); escapeDuck(S.d2);
            S.d1 = null; S.d2 = null;
            S.pairDeadline = 0;
            S.resolveAt = t;
          }
          if (!S.failPending && S.resolveAt > 0 && t >= S.resolveAt) {
            trigBull();
            S.resolveAt = 0;
          }

          if (!S.failPending && !S.bullOn) {
            if (!S.d1 && !S.d2 && !S.h1 && !S.h2 && S.pairN === 0 && S.hits < r.hitsToWin && t >= S.pairSpawn) {
              spawnRoundDucks();
            }
          }

          var dks = [S.d1, S.d2];
          for (var i = 0; i < dks.length; i++) {
            var d = dks[i]; if (!d) continue;

            if (d.rising) {
              // Rise from below the canvas while the foreground is drawn over the duck.
              if (t < d.riseStartAt) continue;
              if (t >= d.riseJukeAt) {
                d.riseJukeDir *= -1;
                d.riseVx = d.riseJukeDir * rand(80, 175) * (d.riseVxScale || 1);
                d.riseSpd = rand(425, 565) * (d.riseSpeedScale || 1);
                d.riseJukeAt = t + rand(d.riseJukeMin || RISE_JUKE_MIN_MS, d.riseJukeMax || RISE_JUKE_MAX_MS);
              }
              var riseWobble = Math.sin((t / 1000) * (Math.PI * 2 * (d.riseWobbleHz || RISE_WOBBLE_HZ)) + d.risePhase) * (d.riseWobbleAmp || RISE_WOBBLE_AMP);
              var riseDx = d.riseVx + riseWobble;
              d.y -= d.riseSpd * dt;
              d.x += riseDx * dt;
              // Keep launches in the playable area even during a hard juke.
              if (d.x < 70) { d.x = 70; d.riseJukeDir = 1; d.riseVx = Math.abs(d.riseVx); }
              else if (d.x > GW - 70) { d.x = GW - 70; d.riseJukeDir = -1; d.riseVx = -Math.abs(d.riseVx); }
              d.facing = riseDx >= 0 ? 1 : -1;
              if (d.y <= d.riseTo) {
                d.y = d.riseTo;
                d.rising = false;
                d.vx = d.riseVx;
                d.vy = -d.riseSpd * 0.45;
                newBurst(d, t);
              }
              continue;
            }

            if (t >= d.bEnd) newBurst(d, t);
            var ease = Math.min(1, (dt * 1000) / TURN_MS);
            d.vx += (d.tVx - d.vx) * ease;
            d.vy += (d.tVy - d.vy) * ease;
            var fl = Math.sin((t / 1000) * (Math.PI * 2 * FLUTTER_HZ) + d.fp) * FLUTTER_AMP;
            d.x += d.vx * dt; d.y += (d.vy + fl) * dt;
            if (d.y < YMIN) { d.y = YMIN; d.tVy = Math.abs(d.tVy) * 0.55 + 80; }
            else if (d.y > YMAX) { d.y = YMAX; d.tVy = -Math.abs(d.tVy) * 0.75 - 120; }
            d.facing = d.vx >= 0 ? 1 : -1;
            if (d.y + d.h < -90 || d.x + d.w < -90 || d.x - d.w > GW + 90) {
              if (d.slot === 1) S.d1 = null; else S.d2 = null;
              S.pairSpawn = t + rand(r.spMin, r.spMax);
            }
          }

          for (var j = S.escs.length - 1; j >= 0; j--) {
            S.escs[j].y -= ESCAPE_SPD * dt;
            if (S.escs[j].y + S.escs[j].h / 2 < -20) S.escs.splice(j, 1);
          }

          // On the final allowed miss, let the full escape animation finish first.
          if (S.failPending && S.escs.length === 0) {
            if (!S.failResetAt) {
              if (ri === LAST_ROUND) {
                // Final round: award the base reward and let the player choose
                // whether to use TRY FOR BONUS for one more attempt.
                S.failAction = "complete";
                S.failResetAt = t + 1000;
                addFlt(GW / 2, GH * 0.35, "HUNT COMPLETE!", "#ff6a13");
              } else {
                // Earlier rounds keep unlimited retries.
                S.failAction = "restart";
                S.failResetAt = t + 1100;
                addFlt(GW / 2, GH * 0.35, "TRY AGAIN!", "#ff6a13");
              }
            } else if (t >= S.failResetAt) {
              var action = S.failAction;
              S.failPending = false;
              S.failResetAt = 0;
              S.failAction = "";
              if (action === "complete") showHuntComplete();
              else startRound();
            }
          }
        }
        upFlts(dt); upConf(dt); upCD(t);

        var sh = getShake(t);
        ctx.save(); ctx.translate(sh.x, sh.y);
        ctx.clearRect(-20, -20, GW + 40, GH + 40);
        drawCover(A.bg);
        if (S.d1) drDuck(A.fly, S.d1.x, S.d1.y, S.d1.w, S.d1.h, S.d1.facing);
        if (S.d2) drDuck(A.fly, S.d2.x, S.d2.y, S.d2.w, S.d2.h, S.d2.facing);
        if (S.h1) drDuck(A.shot, S.sx1, S.sy1, r.dkSz, r.dkSz, S.sf1);
        if (S.h2) drDuck(A.shot, S.sx2, S.sy2, r.dkSz, r.dkSz, S.sf2);
        for (var k = 0; k < S.escs.length; k++) {
          var e = S.escs[k];
          drDuck(A.esc, e.x, e.y, e.w, e.h, e.facing);
        }

        drHitFlash(t); drBull(t); drawGrass(A.grass);
        drMissFlash(t); drFlts(); drSplash(t); drCD(t); drConf();
        ctx.restore();
        rafId = requestAnimationFrame(tick);
      }

      /* ---- CLEANUP HOOK ---- */
      host._cleanup = function () {
        dead = true;
        if (rafId) cancelAnimationFrame(rafId);
        for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
        timers = [];
        canvas.removeEventListener("mousedown", onPtr);
        canvas.removeEventListener("touchstart", onPtr);
        canvas.removeEventListener("touchmove", preventDefault);
        canvas.removeEventListener("touchend", preventDefault);
        canvas.removeEventListener("contextmenu", preventDefault);
        startBtn.removeEventListener("click", onStart);
        playAgainBtn.removeEventListener("click", onPlayAgain);
        copyBtn.removeEventListener("click", onCopy);
        window.removeEventListener("resize", resizeCv);
        if (audioCtx) { try { audioCtx.close(); } catch (e) {} }
      };

      /* ---- INIT ---- */
      resizeCv();
      Promise.all([
        loadImg(ASSETS_SRC.bg), loadImg(ASSETS_SRC.grass),
        loadImg(ASSETS_SRC.bull1 || ASSETS_SRC.bull), loadImg(ASSETS_SRC.fly), loadImg(ASSETS_SRC.shot),
        loadImg(ASSETS_SRC.esc), loadImgOptional(ASSETS_SRC.bull2)
      ]).then(function (loaded) {
        if (dead || !host.isConnected) return;
        A = {
          bg: loaded[0], grass: loaded[1], bull1: loaded[2], fly: loaded[3],
          shot: loaded[4], esc: loaded[5], bull2: loaded[6] || loaded[2]
        };
        setHUD(); showMenu(); rafId = requestAnimationFrame(tick);
      }).catch(function (err) {
        if (dead) return;
        modal.style.display = "flex";
        card.innerHTML =
          "<div style='font-weight:900;font-size:18px;margin-bottom:10px;'>Asset error</div>" +
          "<div style='font-size:14px;'>" + String(err.message || err) + "</div>";
      });
    }

    disconnectedCallback() {
      if (this._cleanup) this._cleanup();
      this._cleanup = null;
      this._booted = false;
    }
  }

  window.customElements.define("duck-huntz-custom", DuckHuntzCustom);
})();
